import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { agentService } from "../../../services/agentService";
import { toast } from 'react-toastify';
import TenantModal from './components/TenentModel';
import { tenantService } from '../../../../src/services/tenantService';
import { agentTenantMapping } from '../../../services/tenantAgentMapping';
import AgentTypeModal from './components/AgentTypeModal';
import { Tabs } from '../../../components/ui/Tabs';
import { Page, PageHeader, Section, Stack } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { TextField } from '../../../components/ui/Field';

type CustomAgentType = {
  type: string;
  description?: string;
  code: string;
  name: string;
  active: boolean;
  capabilities: string[];
  policies: string[];
  metadata?: any;
};

type AgentServer = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  industry?: { id: string; name: string } | null;
  isActive: boolean;
  metadata?: any;
  capabilities: any[];
  policies: any[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

type PaginatedAgents = {
  agentTypes: AgentServer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};


const toCodeSuggestion = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);


// Server DTO shapes (for clarity; adapt if your backend differs)
type ServerCapability = { capabilityCode: string; config: Record<string, any> };
type ServerPolicy = { policyCode: string; rules: Record<string, any> };

type CreateAgentDto = {
  code?: string;
  name: string;
  description?: string;
  isActive: boolean;
  metadata?: Record<string, any>;
  capabilities: ServerCapability[];
  policies: ServerPolicy[];
};

type UpdateAgentDto = Partial<CreateAgentDto>;

// Safely parse JSON after a ":" suffix (e.g. sentiment:{"threshold":0.7})
const parseSuffixJSON = (raw: string) => {
  const idx = raw.indexOf(":");
  if (idx === -1) return { key: raw.trim(), obj: {} };
  const key = raw.slice(0, idx).trim();
  const maybeJson = raw.slice(idx + 1).trim();
  try {
    const obj = maybeJson ? JSON.parse(maybeJson) : {};
    return { key, obj };
  } catch {
    console.warn("Invalid JSON after ':' in", raw, "— sending empty config/rules");
    return { key, obj: {} };
  }
};

// Map UI fields → server payload
const toServerPayload = ({
  code,
  name,
  desc,
  active,
  capabilities,
  policies,
  metadataObj,
}: {
  code?: string;
  name: string;
  desc?: string;
  active: boolean;
  capabilities: string[];
  policies: string[];
  metadataObj?: any;
}): CreateAgentDto => {
  const caps: ServerCapability[] = capabilities.map((c) => {
    const { key, obj } = parseSuffixJSON(c);
    return { capabilityCode: key, config: obj };
  });
  const pols: ServerPolicy[] = policies.map((p) => {
    const { key, obj } = parseSuffixJSON(p);
    return { policyCode: key, rules: obj };
  });

  return {
    ...(code ? { code } : {}),
    name,
    description: desc || undefined,
    isActive: active,
    metadata: metadataObj || undefined,
    capabilities: caps,
    policies: pols,
  };
};


const AgentType: React.FC = () => {
  const [activeIndustryTab, setActiveIndustryTab] = useState<string>('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTypeCode, setNewTypeCode] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');
  const [newTypeActive, setNewTypeActive] = useState(true);
  const [newTypeCapabilities, setNewTypeCapabilities] = useState<string[]>([]);
  const [newTypePolicies, setNewTypePolicies] = useState<string[]>([]);
  const [newTypeMetadata, setNewTypeMetadata] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customTypes, setCustomTypes] = useState<CustomAgentType[]>([]);
  const [tenentModelOpen, setTenentModelOpen] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState()


  // editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editOriginalTypeName, setEditOriginalTypeName] = useState<string | null>(null);


  // ...inside your component:
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [saving, setSaving] = useState(false);

  // server-backed listing
  const [serverTypes, setServerTypes] = useState<AgentServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tenants, setTenants] = useState<any[]>([]);

  // server filters (align to your endpoint)
  const [onlyActive, setOnlyActive] = useState<boolean | undefined>(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');



  const refreshList = async () => {
    setLoading(true);
    try {
      const data = await agentService.listAgentTypes({

        page,
        search: searchQuery || "",
        isActive: typeof onlyActive === "boolean" ? onlyActive : undefined,
        includeDeleted,
      });

      // data may be { data: {...} } or direct; normalize:
      const payload: PaginatedAgents = (data as any)?.agentTypes
        ? (data as any)
        : (data as any)?.data ?? (data as any);

      setServerTypes(payload.agentTypes || []);
      setTotal(payload.total || 0);
      setTotalPages(payload.totalPages || 1);
    } catch (e) {
      console.error("Failed to load agent types:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveTenantData = async (dataenantIds: string[], agentTypeId: string) => {
    try {
      const payload = {
        tenantTypeIds: dataenantIds,
        agentTypeId: agentTypeId
      }
      const resopnse = await agentTenantMapping.createTenantAgentMapping(payload)
      if (resopnse) {
        toast.success("Tenent agent modified")
      }
    }
    catch (error: any) {
      toast.error(error.data)
    }
  }


  const loadTenants = async () => {
    try {
      setLoading(true);
      const res: any = await tenantService.list();
      setTenants(res?.tenants || []);
    } catch (err) {
      console.error("Failed to load tenants", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    let t = setTimeout(() => {
      refreshList();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, onlyActive, includeDeleted, searchQuery]);


  const serverByName = useMemo(() => {
    const m = new Map<string, AgentServer>();
    for (const s of serverTypes) m.set((s.name || "").trim(), s);
    return m;
  }, [serverTypes]);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [agentTypeId, setAgentTypeId] = useState<string>();


  const askDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await agentService.deleteAgentType(deleteId);
      toast.success("Deleted successfully")

      await refreshList();
    } catch (e) {
      console.error("❌ Server delete failed:", e);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  // Load custom types from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('customAgentTypes');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const normalized: CustomAgentType[] = parsed
            .filter((t: any) => typeof t?.type === 'string')
            .map((t: any) => ({
              type: t.type,
              description: t.description,
              code: t.code || '',
              name: t.name || t.type,
              active: typeof t.active === 'boolean' ? t.active : true,
              capabilities: Array.isArray(t.capabilities) ? t.capabilities : [],
              policies: Array.isArray(t.policies) ? t.policies : [],
              metadata: t.metadata,
            }));
          setCustomTypes(normalized);
        }
      }
    } catch {

    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('customAgentTypes', JSON.stringify(customTypes));
    } catch {

    }
  }, [customTypes]);

  const industryTabs = useMemo(() => {
    const unique = Array.from(
      new Set(
        serverTypes
          .map((item) => item.industry?.name?.trim())
          .filter((name): name is string => !!name)
      )
    ).sort((a, b) => a.localeCompare(b));

    return ['ALL', ...unique];
  }, [serverTypes]);

  const filteredServerTypes = useMemo(() => {
    if (activeIndustryTab === 'ALL') return serverTypes;
    return serverTypes.filter(
      (item) => (item.industry?.name?.trim() || '') === activeIndustryTab
    );
  }, [serverTypes, activeIndustryTab]);

  useEffect(() => {
    if (!industryTabs.includes(activeIndustryTab)) {
      setActiveIndustryTab('ALL');
    }
  }, [industryTabs, activeIndustryTab]);

  const resetForm = () => {
    setNewTypeCode('');
    setNewTypeName('');
    setNewTypeDescription('');
    setNewTypeActive(true);
    setNewTypeCapabilities([]);
    setNewTypePolicies([]);
    setNewTypeMetadata('');
    setError(null);
    setIsEditing(false);
    setEditOriginalTypeName(null);
  };

  const openCreateModal = () => {
    resetForm();
    setSelectedAgentType(undefined);
    setIsModalOpen(true);
  };

  const openEditModal = (agt: any) => {
    setSelectedAgentType(agt)
    setEditId(agt?.id);
    setError(null);
    setIsEditing(true);

    setEditOriginalTypeName(agt.name || agt.code);

    setNewTypeCode(agt.code || '');
    setNewTypeName(agt.name || '');
    setNewTypeDescription(agt.description || '');
    setNewTypeActive(!!agt.isActive);


    const caps = (agt.capabilities || []).map((c: any) => {
      if (typeof c === 'string') return c;
      const code = c.capabilityCode || c.code || c.name || '';
      const cfg = c.config && Object.keys(c.config).length ? `:${JSON.stringify(c.config)}` : '';
      return `${code}${cfg}`;
    });
    const pols = (agt.policies || []).map((p: any) => {
      if (typeof p === 'string') return p;
      const code = p.policyCode || p.code || p.name || '';
      const rules = p.rules && Object.keys(p.rules).length ? `:${JSON.stringify(p.rules)}` : '';
      return `${code}${rules}`;
    });

    setNewTypeCapabilities(caps);
    setNewTypePolicies(pols);

    setNewTypeMetadata(agt.metadata ? JSON.stringify(agt.metadata, null, 2) : '');

    setIsModalOpen(true);
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    const code = newTypeCode.trim().toUpperCase();
    const name = newTypeName.trim();
    const desc = newTypeDescription.trim();
    const localCode = code || toCodeSuggestion(name) || "AGENT";

    if (!name) {
      setError("Type name is required.");
      return;
    }

    let metadataObj: any = undefined;
    if (newTypeMetadata.trim()) {
      try {
        metadataObj = JSON.parse(newTypeMetadata);
      } catch {
        setError("Invalid JSON in metadata.");
        return;
      }
    }

    const cleanedCapabilities = Array.from(
      new Set(newTypeCapabilities.map((c) => c.trim()).filter(Boolean))
    );
    const cleanedPolicies = Array.from(
      new Set(newTypePolicies.map((p) => p.trim()).filter(Boolean))
    );

    // ------- Build payloads -------
    const consolePayload = {
      code,
      name,
      type: name,
      description: desc || undefined,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadata: metadataObj,
    };

    const serverPayload = toServerPayload({
      code,
      name,
      desc,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadataObj,
    });


    // ------- Optimistic local update (keep cards instant) -------
    const newItem: CustomAgentType = {
      type: name,
      name,
      code: localCode,
      description: desc || undefined,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadata: metadataObj,
    };

    if (isEditing && editOriginalTypeName) {
      setCustomTypes((prev) => {
        const idx = prev.findIndex(
          (ct) => ct.type.toLowerCase() === editOriginalTypeName.toLowerCase()
        );
        if (idx === -1) return [...prev, newItem];
        const copy = [...prev];
        copy[idx] = newItem;
        return copy;
      });
    } else {
      setCustomTypes((prev) => [...prev, newItem]);
    }

    setSaving(true);
    try {
      if (isEditing && editOriginalTypeName) {
        const res = await agentService.updateAgentType(String(editId), serverPayload);
        toast.success("Updated successfully")
        await refreshList()
      } else {
        const res = await agentService.createAgentType(serverPayload);
        toast.success("Created successfully")
        await refreshList()

        if (res && (res as any).id) {
          setCustomTypes((prev) =>
            prev.map((ct) =>
              ct.code === localCode
                ? {
                  ...ct,
                  metadata: {
                    ...(ct.metadata || {}),
                    id: (res as any).id,
                  },
                }
                : ct
            )
          );
        } else if (res && (res as any)._id) {
          setCustomTypes((prev) =>
            prev.map((ct) =>
              ct.code === localCode
                ? {
                  ...ct,
                  metadata: {
                    ...(ct.metadata || {}),
                    _id: (res as any)._id,
                  },
                }
                : ct
            )
          );
        }
      }
    } catch (err) {
      console.error("❌ Save failed:", err);

    } finally {
      setSaving(false);
    }

    setIsModalOpen(false);
    resetForm();
  };

  return (
    <Page contentClassName="pt-0 pb-8">
      <div className="border-b border-white/30 bg-white/80 shadow-sm backdrop-blur-sm">
        <PageHeader
          className="py-6"
          titleClassName="truncate bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-3xl font-bold text-transparent md:text-3xl"
          subtitleClassName="mt-2 text-agentic-text-secondary text-base"
          title="Agent Types"
          subtitle="Browse all available agent types in your workspace."
          aside={
            <div className="flex w-full max-w-xl flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
              <div className="relative w-full max-w-sm flex-1">
                <TextField tone="default-focus-primary"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search agent types…"
                  className="w-full rounded-xl border py-2 pl-10 pr-3 focus:ring-2"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              </div>

              <Button tone="primary-gradient"
                onClick={() => setIsModalOpen(true)}
                className="shrink-0 rounded-xl px-4 py-2 transition-all"
              >
                + New Agent Type
              </Button>
            </div>
          }
        />
      </div>

      <Section className="py-8">
        <Stack space="lg">
            <Tabs
              variant="underline"
              items={industryTabs.map((tab) => ({ id: tab, label: tab }))}
              value={activeIndustryTab}
              onChange={setActiveIndustryTab}
            />

            {loading ? (
              <div className="py-16 text-center">Loading…</div>
            ) : filteredServerTypes.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mb-4 text-6xl">🏷️</div>
                <h3 className="mb-2 text-xl font-semibold text-agentic-text-primary">
                  No agent types found
                </h3>
                <p className="text-agentic-text-secondary">
                  {activeIndustryTab === 'ALL'
                    ? 'Create an agent type to get started.'
                    : `No agent types found for ${activeIndustryTab}.`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredServerTypes.map((agt) => (
                  <Card
                    key={agt.id}
                    hover
                    className="rounded-2xl"
                    title={
                      <div className="flex min-w-0 items-center justify-between gap-2">
                        <span className="min-w-0 flex-1 truncate">{agt.name || agt.code}</span>
                        <div className="flex items-center gap-2">
                          <Button tone="outline"
                            type="button"
                            onClick={() => openEditModal(agt)}
                            className="rounded-lg border px-2 py-1 text-sm"
                            title="Edit"
                          >
                            Edit
                          </Button>
                          <Button tone="danger-soft"
                            type="button"
                            onClick={() => askDelete(agt.id)}
                            className="rounded-lg border px-2 py-1 text-sm"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    }
                    subtitle={`${agt.code} • ${agt.isActive ? 'Active' : 'Inactive'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light text-white">
                          <span className="text-lg">🏷️</span>
                        </div>
                        <div className="text-sm text-agentic-text-secondary">
                          {agt.description ? (
                            <span>{agt.description}</span>
                          ) : (
                            <span>Created {new Date(agt.createdAt || '').toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-end">
                      <Button tone="primary-gradient"
                        className="shrink-0 rounded-xl px-4 py-2 transition-all"
                        onClick={() => {
                          if (agt.isActive) {
                            setTenentModelOpen(true);
                            setAgentTypeId(agt.id);
                          } else {
                            toast.warning("Agent type is inactive");
                          }
                        }}
                      >
                        Assign Tenant
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Stack>
      </Section>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <AgentTypeModal
          isOpen={isModalOpen}
          isEditing={isEditing}
          agentTypeDetails={selectedAgentType}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
            setSelectedAgentType(undefined)
          }}
          refreshList={refreshList}
          // setCustomTypes={setCustomTypes}
        />
      )}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this agent type?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />

      <TenantModal
        isOpen={tenentModelOpen}
        onClose={() => setTenentModelOpen(false)}
        onSave={saveTenantData}
        tenants={tenants}
        agentTypeId={agentTypeId || ""}
      />
    </Page>
  );
};

export default AgentType;
